/**
 * Datasource local Plans: abstração + implementação localforage.
 */

import localforage from 'localforage';
import { Plan } from '@/core/domain';

const CACHE_KEY = 'retornaar:plans';

export interface PlanLocalDataSource {
  getById(id: string): Promise<Plan | null>;
  getAll(): Promise<Plan[]>;
  create(plan: Plan): Promise<Plan>;
  update(id: string, plan: Partial<Plan>): Promise<Plan>;
  delete(id: string): Promise<void>;
  clear(): Promise<void>;
}

const store = localforage.createInstance({ name: 'retornaar', storeName: 'plans' });

export class PlanLocalForageDataSource implements PlanLocalDataSource {
  private async getMap(): Promise<Record<string, Plan>> {
    const raw = await store.getItem<Record<string, Plan>>(CACHE_KEY);
    return raw ?? {};
  }

  private async setMap(map: Record<string, Plan>): Promise<void> {
    await store.setItem(CACHE_KEY, map);
  }

  async getById(id: string): Promise<Plan | null> {
    const map = await this.getMap();
    return map[id] ?? null;
  }

  async getAll(): Promise<Plan[]> {
    const map = await this.getMap();
    return Object.values(map);
  }

  async create(plan: Plan): Promise<Plan> {
    const map = await this.getMap();
    map[plan.id] = plan;
    await this.setMap(map);
    return plan;
  }

  async update(id: string, plan: Partial<Plan>): Promise<Plan> {
    const map = await this.getMap();
    const current = map[id];
    if (!current) throw new Error('Plan not found');
    const updated = { ...current, ...plan, id };
    map[id] = updated;
    await this.setMap(map);
    return updated;
  }

  async delete(id: string): Promise<void> {
    const map = await this.getMap();
    delete map[id];
    await this.setMap(map);
  }

  async clear(): Promise<void> {
    await store.removeItem(CACHE_KEY);
  }
}
